import React, { useState } from 'react';
import { getSessionHeaders } from '../lib/serverSession';
import ProfileImageCropper from './ProfileImageCropper';
import { SignedProfileImage } from './SignedProfileImage';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

interface ProfilePictureUploadEnhancedProps {
  currentImageUrl?: string;
  userId: number;
  onImageUpdate: (newImageUrl: string) => void;
  size?: 'small' | 'medium' | 'large';
  editable?: boolean;
  userInitials?: string;
  userName?: string;
  showSaveButton?: boolean;
  autoSave?: boolean;
}

export const ProfilePictureUploadEnhanced: React.FC<ProfilePictureUploadEnhancedProps> = ({
  currentImageUrl,
  userId,
  onImageUpdate,
  size = 'medium',
  editable = true,
  userInitials = 'U',
  userName = 'User',
  showSaveButton = false,
  autoSave = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sizeClasses = {
    small: { container: '48px', text: '18px' },
    medium: { container: '80px', text: '28px' },
    large: { container: '120px', text: '40px' }
  };

  const currentSize = sizeClasses[size];

  // Clear messages after 3 seconds
  React.useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  /** Upload an optimized blob (from cropper) to storage */
  const uploadImageToStorage = async (blob: Blob): Promise<string> => {
    console.log('📤 Uploading cropped profile image:', { size: blob.size, type: blob.type, userId });

    // Convert optimized blob to base64 (~50-150KB for 500×500 JPEG)
    const base64Data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

    const response = await fetch(`${API_BASE_URL}/api/user/profile-completion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({
        userId,
        operation: 'upload_profile_picture',
        data: { fileBase64: base64Data, fileName: 'profile.jpg', fileType: 'image/jpeg' }
      })
    });

    // Safe JSON parse — prevents "is not valid JSON" crash
    let result: any;
    try { result = await response.json(); }
    catch { throw new Error('Server returned an invalid response. Please try again.'); }
    if (!response.ok) throw new Error(`Upload failed: ${result.error || 'Server error'}`);

    const publicUrl = result.data?.profilePictureUrl;
    if (!publicUrl) throw new Error('No profile picture URL returned from server');
    console.log('✅ Profile picture uploaded via API:', publicUrl);
    return publicUrl;
  };

  const saveProfilePicture = async (imageUrl: string) => {
    // Update user record in database via API endpoint
    console.log('🔍 Updating profile picture in database for user ID:', userId, 'Type:', typeof userId);

    const response = await fetch(`${API_BASE_URL}/api/user/update-profile`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
      body: JSON.stringify({
        userId,
        operation: 'update_profile_picture_url',
        data: { profileImageUrl: imageUrl }
      })
    });

    const result = await response.json();
    if (!response.ok) {
      console.error('❌ Database update error:', result.error);
      throw new Error(`Database update failed: ${result.error}`);
    }

    console.log('✅ Profile picture updated in database:', imageUrl);
    console.log('✅ Database update result:', result.data);

    // Old image cleanup is handled server-side by the profile-completion API

    // Update localStorage with new profile picture URL and timestamp
    // FIXED: Update both root level AND database_user level to ensure consistency
    // Dashboard reads from user.database_user.profile_image_url
    const currentTimestamp = new Date().toISOString();
    const storedUser = localStorage.getItem('aureus_user');
    if (storedUser) {
      const userData = JSON.parse(storedUser);
      // Update root level for backward compatibility
      userData.profile_image_url = imageUrl;
      userData.updated_at = currentTimestamp;
      // CRITICAL FIX: Also update database_user if it exists (dashboard reads from here)
      if (userData.database_user) {
        userData.database_user.profile_image_url = imageUrl;
        userData.database_user.updated_at = currentTimestamp;
      }
      // Also update user_metadata if it exists
      if (userData.user_metadata) {
        userData.user_metadata.profile_image_url = imageUrl;
      }
      localStorage.setItem('aureus_user', JSON.stringify(userData));
      console.log('✅ Profile picture URL updated in localStorage (root + database_user) with timestamp');
    }

    onImageUpdate(imageUrl);
  };

  /** Called by ProfileImageCropper with an optimized 500×500 JPEG blob */
  const handleCropped = async (blob: Blob) => {
    setUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const publicUrl = await uploadImageToStorage(blob);

      if (autoSave) {
        await saveProfilePicture(publicUrl);
        setSuccess('Profile picture updated successfully!');
      } else {
        setPendingImageUrl(publicUrl);
        setHasUnsavedChanges(true);
        setSuccess('Image uploaded! Click Save to apply changes.');
      }
    } catch (err: any) {
      console.error('Profile picture upload error:', err);
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!pendingImageUrl) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await saveProfilePicture(pendingImageUrl);
      setPendingImageUrl(null);
      setHasUnsavedChanges(false);
      setSuccess('Profile picture saved successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to save profile picture');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPendingImageUrl(null);
    setHasUnsavedChanges(false);
    setError(null);
    setSuccess(null);
  };

  const displayImageUrl = pendingImageUrl || currentImageUrl;

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Profile Picture Container — wrapped in cropper */}
      <ProfileImageCropper
        onCropped={handleCropped}
        disabled={!editable || uploading || saving}
        trigger={
      <div
        style={{
          width: currentSize.container,
          height: currentSize.container,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: editable ? 'pointer' : 'default',
          position: 'relative',
          background: displayImageUrl 
            ? 'transparent' 
            : 'linear-gradient(135deg, #FFD700 0%, #E6C200 100%)',
          border: hasUnsavedChanges 
            ? '3px solid #FFA500' 
            : '3px solid rgba(255, 215, 0, 0.3)',
          boxShadow: hasUnsavedChanges 
            ? '0 0 18px rgba(255, 165, 0, 0.5)' 
            : '0 0 18px rgba(255, 215, 0, 0.3)',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (editable) {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = hasUnsavedChanges 
              ? '0 0 24px rgba(255, 165, 0, 0.7)' 
              : '0 0 24px rgba(255, 215, 0, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (editable) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = hasUnsavedChanges 
              ? '0 0 18px rgba(255, 165, 0, 0.5)' 
              : '0 0 18px rgba(255, 215, 0, 0.3)';
          }
        }}
      >
        {pendingImageUrl ? (
          <img
            src={pendingImageUrl}
            alt={`${userName} Profile Picture`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <SignedProfileImage
            rawUrl={currentImageUrl}
            alt={`${userName} Profile Picture`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
            fallback={
          <div style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#000000',
            fontSize: currentSize.text,
            fontWeight: 'bold'
          }}>
            {userInitials}
          </div>
            }
          />
        )}

        {/* Loading Overlay */}
        {(uploading || saving) && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%'
          }}>
            <div style={{
              width: '24px',
              height: '24px',
              border: '2px solid #FFD700',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
          </div>
        )}

        {/* Unsaved Changes Indicator */}
        {hasUnsavedChanges && (
          <div style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            width: '12px',
            height: '12px',
            backgroundColor: '#FFA500',
            borderRadius: '50%',
            border: '2px solid #000',
            animation: 'pulse 2s infinite'
          }}></div>
        )}
      </div>
        } /* end trigger */
      />

      {/* Save/Cancel Buttons */}
      {showSaveButton && hasUnsavedChanges && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          display: 'flex',
          gap: '8px',
          zIndex: 10
        }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              backgroundColor: '#FFD700',
              color: '#000',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              fontWeight: 'bold',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleCancel}
            disabled={saving}
            style={{
              backgroundColor: '#666',
              color: '#fff',
              border: 'none',
              padding: '6px 12px',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Status Messages */}
      {(error || success) && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: showSaveButton && hasUnsavedChanges ? '48px' : '8px',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '14px',
          fontWeight: 'bold',
          whiteSpace: 'nowrap',
          zIndex: 10,
          backgroundColor: error ? '#ff4444' : '#44ff44',
          color: '#000'
        }}>
          {error || success}
        </div>
      )}

      {/* CSS Animation */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};
